/**
 * Part B4 — Bulk qualifier (TrustX® dimension) editor.
 *
 * Route: /surveys/:surveyId/qualifiers   (platform_admin or tenant_admin)
 *
 * Survey-agnostic. Lists every question in the survey with its current
 * `qualifier_id` resolved to a label, lets the admin re-tag in bulk, and
 * sends a single PATCH to /api/surveys/builder/:surveyId/questions/bulk-qualifiers
 * containing ONLY the changed rows.
 *
 * Hard guarantee: never overwrites an existing qualifier_id silently — the
 * picker is pre-populated from the DB value, and only changed rows are sent.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import QualifierPill from '@/components/QualifierPill';
import { cleanQuestionLabel, getQuestionLabel, getQuestionType } from '@/lib/answerFormatting';
import { computeQuestionNumbers } from '@/lib/questionNumbering';

const NOT_SCORED = '__not_scored__';
const EXCLUDED_TYPES = new Set(['welcome', 'thank_you']);

function TypeBadge({ type }) {
  const label = (type || 'question').replace(/_/g, ' ');
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]">{label}</span>;
}

export default function QualifierBulkEditor() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [picks, setPicks] = useState({}); // qid → qualifier_id | NOT_SCORED
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  const allowed = ['platform_admin', 'super_admin', 'tenant_admin'].includes(user?.role);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      // Single endpoint — the builder GET returns the survey config AND its
      // questions in one payload. (There is no separate /questions GET.)
      // Backend allows platform_admin / super_admin to fetch by survey_id
      // without a tenant_id filter; tenant-level roles are gated by tenant.
      const res = await fetch(
        `${API}/surveys/builder/${encodeURIComponent(surveyId)}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.text()).slice(0, 300); } catch { /* noop */ }
        console.error('[QualifierBulkEditor] fetch failed', {
          surveyId,
          userRole: user?.role,
          status: res.status,
          ok: res.ok,
          body: detail,
        });
        setLoadError('Unable to load questions. Please try again.');
        return;
      }
      const s = await res.json();
      const items = Array.isArray(s.questions) ? s.questions : [];
      setSurvey(s);
      setQuestions(items);
      // Pre-populate picks from saved values.
      const initial = {};
      for (const it of items) {
        const qid = it.id || it.question_id;
        if (!qid) continue;
        initial[qid] = it.qualifier_id || NOT_SCORED;
      }
      setPicks(initial);
    } catch (err) {
      console.error('[QualifierBulkEditor] fetch threw', err);
      setLoadError('Unable to load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [surveyId, user?.role]);

  useEffect(() => {
    if (!allowed) return;
    loadData();
  }, [allowed, loadData]);

  const qualifiers = survey?.qualifiers || [];
  const numberMap = useMemo(() => computeQuestionNumbers(questions), [questions]);

  // Rows to render — exclude welcome/thank_you entirely; section headers
  // render as a divider with no dropdown.
  const visible = useMemo(() => (questions || []).filter(q => !EXCLUDED_TYPES.has(getQuestionType(q))), [questions]);

  const changedCount = useMemo(() => {
    let n = 0;
    for (const q of visible) {
      const qid = q.id || q.question_id;
      const current = q.qualifier_id || NOT_SCORED;
      if ((picks[qid] || NOT_SCORED) !== current) n += 1;
    }
    return n;
  }, [picks, visible]);

  const onSave = async () => {
    const updates = [];
    for (const q of visible) {
      if (getQuestionType(q) === 'section_header') continue;
      const qid = q.id || q.question_id;
      const current = q.qualifier_id || NOT_SCORED;
      const chosen = picks[qid] || NOT_SCORED;
      if (chosen === current) continue;
      updates.push({
        question_id: qid,
        qualifier_id: chosen === NOT_SCORED ? null : chosen,
      });
    }
    if (!updates.length) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/surveys/builder/${encodeURIComponent(surveyId)}/questions/bulk-qualifiers`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
        return;
      }
      const data = await res.json();
      // Patch local state with the saved values
      setQuestions(prev => prev.map(q => {
        const qid = q.id || q.question_id;
        const u = updates.find(x => x.question_id === qid);
        return u ? { ...q, qualifier_id: u.qualifier_id } : q;
      }));
      if (data.failed && data.failed.length) {
        toast.warning(`${data.updated} saved, ${data.failed.length} failed`);
      } else {
        toast.success(`All dimension tags saved (${data.updated})`);
      }
    } catch {
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  if (!allowed) {
    return (
      <DashboardLayout>
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-3" />
          <p className="font-medium text-[#0F172A]">Platform admin or tenant admin access required</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-24 text-[#64748B] gap-3" data-testid="qualifier-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#1A3E6F]" />
          <p className="text-sm">Loading questions…</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center max-w-md mx-auto space-y-4" data-testid="qualifier-load-error">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
          <p className="font-medium text-[#0F172A]">Unable to load questions. Please try again.</p>
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/survey-builder/${surveyId}`)}
              data-testid="qualifier-error-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to builder
            </Button>
            <Button
              size="sm"
              onClick={loadData}
              className="bg-[#1A3E6F] hover:bg-[#0F2A4F] text-white"
              data-testid="qualifier-retry"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Retry
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qualifier-bulk-editor">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/survey-builder/${surveyId}`)} data-testid="qualifier-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to builder
            </Button>
            <h1 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A] mt-2">{survey?.name || 'Survey'}</h1>
            <p className="text-sm text-[#64748B]">Dimension tagging</p>
          </div>
          <Button
            onClick={onSave}
            disabled={saving || changedCount === 0}
            className="bg-[#1A3E6F] hover:bg-[#0F2A4F] text-white"
            data-testid="qualifier-save"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : (changedCount > 0 ? `Save all changes (${changedCount})` : 'Save all changes')}
          </Button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#475569]">
          <span className="font-semibold">Legend:</span>
          <QualifierPill label="Brand Excellence" />
          <QualifierPill label="Sales Excellence" />
          <QualifierPill label="Service Excellence" />
          <span className="inline-flex items-center rounded-full font-semibold uppercase tracking-wide px-2 py-0.5 text-[10px] bg-[#F1F5F9] text-[#64748B]">Not scored</span>
        </div>

        {qualifiers.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900" data-testid="qualifier-empty-warning">
            This survey has no TrustX® qualifiers defined yet. Add them via the builder's Qualifiers panel first.
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
          <table className="w-full text-sm" data-testid="qualifier-table">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-4 py-2 w-16">#</th>
                <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-4 py-2 w-32">Type</th>
                <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-4 py-2">Question</th>
                <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-4 py-2 w-64">Dimension</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((q) => {
                const qid = q.id || q.question_id;
                const qtype = getQuestionType(q);
                if (qtype === 'section_header') {
                  return (
                    <tr key={qid} className="bg-[#F1F5F9]">
                      <td colSpan={4} className="px-4 py-2 font-semibold text-[#1A3E6F] uppercase text-[11px] tracking-wider">
                        Section · {cleanQuestionLabel(q.text || q.question_text || 'Section')}
                      </td>
                    </tr>
                  );
                }
                const number = numberMap[qid];
                const current = picks[qid] || NOT_SCORED;
                return (
                  <tr key={qid} className="border-b border-[#F1F5F9] last:border-b-0" data-testid={`qualifier-row-${qid}`}>
                    <td className="px-4 py-2 text-[#64748B] font-mono text-xs">{number || '—'}</td>
                    <td className="px-4 py-2"><TypeBadge type={qtype} /></td>
                    <td className="px-4 py-2 text-[#0F172A]">{getQuestionLabel(q)}</td>
                    <td className="px-4 py-2">
                      <Select value={current} onValueChange={(v) => setPicks(prev => ({ ...prev, [qid]: v }))}>
                        <SelectTrigger className="w-full" data-testid={`qualifier-select-${qid}`}>
                          <SelectValue placeholder="Choose dimension" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NOT_SCORED} data-testid={`qualifier-option-${qid}-not-scored`}>Not scored</SelectItem>
                          {qualifiers.map(qual => (
                            <SelectItem key={qual.id} value={qual.id} data-testid={`qualifier-option-${qid}-${(qual.label || '').toLowerCase().replace(/\s+/g, '-')}`}>
                              {qual.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
